import React, { useState, useEffect, useCallback, useRef } from 'react';
import OcrGroupDetail from '../components/OcrGroupDetail';
import Modal, { useConfirm } from '../components/Modal';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from '../components/Toast';
import TabBar from '../components/TabBar';
import BatchUploadModal from '../components/BatchUploadModal'; // [FIX #11] renamed from ImageUploadModal
import TutorialButton from '../components/TutorialButton';
import BizSwitch from '../components/BizSwitch';
import PuzzleWorkspace from '../components/PuzzleWorkspace';
import { useBizMode } from '../utils/bizMode';
import api from '../utils/api';
import { bjMinuteOfDay, filenameTimestamp, calcExpiryFromRecords } from '../utils/datetime';
import {
  Settings, CheckCircle2, ArrowRight, PackageOpen,
  AlertTriangle, Clock, Copy, ClipboardList, ChevronDown, ChevronUp,
  Camera, Edit3, X, Loader, Bell, Share2,
  Images, FolderPen, UploadCloud, Lock, ScanLine
} from 'lucide-react';
import PushInbox from '../components/PushInbox';
import usePushInbox from '../utils/usePushInbox';
import { copyToClipboard } from '../utils/clipboard';
import { buildXlsx, buildNumberText, shareFile } from '../utils/share';
import { playChime, supportsAudio, unlockAudio } from '../utils/notifySound';
import { ensurePushSubscription, silentResubscribe, supportsPush, currentNotifyPermission } from '../utils/webpush';

// ── 上传悬浮球拖拽 ───────────────────────────────────────────────────────────
const FAB_SIZE = 56;
const FAB_KEY  = 'ws_fab_pos';
const DRAG_THRESHOLD = 6; // px，移动超过该距离才视为拖拽，否则视为点击

function loadFabPos() {
  try {
    const s = localStorage.getItem(FAB_KEY);
    if (s) {
      const p = JSON.parse(s);
      if (typeof p.x === 'number' && typeof p.y === 'number') return p;
    }
  } catch {}
  // 默认位置：右下角（right:20 bottom:80）
  return {
    x: (typeof window !== 'undefined' ? window.innerWidth  : 375) - FAB_SIZE - 20,
    y: (typeof window !== 'undefined' ? window.innerHeight : 667) - FAB_SIZE - 80,
  };
}

function clampFab(x, y) {
  const pad  = 4;
  const maxX = window.innerWidth  - FAB_SIZE - pad;
  const maxY = window.innerHeight - FAB_SIZE - pad;
  return { x: Math.max(pad, Math.min(x, maxX)), y: Math.max(pad, Math.min(y, maxY)) };
}

export default function WorkspacePage() {
  const { isAdminOrAgent, user } = useAuth();
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [bizMode] = useBizMode(); // 排五 / 证券

  const copy = async (text, label = '已复制') => {
    await copyToClipboard(text);
    showToast(label, 'success');
  };

  const [summary, setSummary] = useState(null);
  const [activeData, setActiveData] = useState([]);
  const [hasActiveTask, setHasActiveTask] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [dataExpired, setDataExpired] = useState(false);   // 当前批次是否已超过72h有效期
  const [dataExpiryAt, setDataExpiryAt] = useState(null); // 有效期截止时间字符串
  const [loading, setLoading] = useState(true);
  const [workSettings, setWorkSettings] = useState(null);
  const [workTimeEnded, setWorkTimeEnded] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  // 已点击/复制过的手机号 id 集合（持久化到本次 session + localStorage，跨刷新保留）
  const [clickedPhones, setClickedPhones] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ws_clicked_phones') || '[]')); }
    catch { return new Set(); }
  });

  // 弹窗状态：{ batchId, batchLabel, mode }
  const [uploadModal, setUploadModal] = useState(null);
  // 完成后上传引导：{ batchId, batchLabel }
  const [postCompleteUpload, setPostCompleteUpload] = useState(null);

  // 数据列表折叠态（超过 20 条默认折叠）
  const [dataCollapsed, setDataCollapsed] = useState(false);

  // 今日上传分组列表 [{ id, seq, image_count, is_exported }]
  const [uploadGroups, setUploadGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({}); // groupId -> bool 展开
  const [deletingGroupId, setDeletingGroupId] = useState(null); // 正在删除中的批次 id
  const [ocrManualTarget, setOcrManualTarget] = useState(null); // { imageId, groupId }
  const [ocrManualPhone, setOcrManualPhone] = useState('');
  const [ocrManualLoading, setOcrManualLoading] = useState(false);
  const [ocrManualCheck, setOcrManualCheck] = useState(null); // 派单库校验结果
  // 纠错模式：{ batchId, batchLabel, images: [{id,url,...}] }
  const [correction, setCorrection] = useState(null);
  // 纠错模式加载状态：'loading' | 'ready'
  const [correctionLoading, setCorrectionLoading] = useState(false);
  // 灯箱：{ images, idx } 任意页面共用，点击图片全屏预览
  const [lightbox, setLightbox] = useState(null);
  // 站内通知收件箱：没处理的通知都放在里面，弹层里上一条/下一条翻页，可一键全部关闭（逻辑见 utils/usePushInbox.js）
  const { list: pushList, index: pushIdx, newCount: pushNew, addPushes, goTo: goPush, closeOne: closePushOne, closeAll: closePushAll } = usePushInbox();
  // 页内横幅：前台/后台均显示（3.5s 自动消失），与 AdminNotificationBell 的 popup 对齐
  const [pushBanner, setPushBanner] = useState(null);
  const pushBannerTimer = useRef(null);
  // 悬浮球拖拽：位置（left/top）持久化到 localStorage
  const [fabPos, setFabPos] = useState(loadFabPos);
  const [fabPulse, setFabPulse] = useState(false); // 完成后闪烁引导上传
  const fabDrag = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  // OCR 完成气泡通知：{ stats, groupUUID, seq }
  const [ocrDoneBanner, setOcrDoneBanner] = useState(null);
  const ocrBannerTimer = useRef(null);

  // 登录后处理 Web Push 订阅：
  //   - 已授权：静默重确认（处理换设备/清除数据/重装后订阅丢失的情况）
  //   - APK 内且未决定：自动弹系统权限框（Capacitor WebView 不需要用户手势）
  //   - 浏览器且未决定：保留手动触发（避免打扰桌面用户）
  useEffect(() => {
    if (!user) return;
    silentResubscribe();

    // APK 内自动申请 Web Push 权限 + 订阅
    // Capacitor 在原生模式下会把自己注入到 window.Capacitor，无需 npm 包即可检测
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    if (isNative && currentNotifyPermission() === 'default') {
      // 延迟 1.5 秒，让页面先渲染完稳定后再弹权限框
      setTimeout(async () => {
        const ok = await ensurePushSubscription();
        setNotifyPerm(currentNotifyPermission());
        if (ok) showToast('已开启消息通知', 'success');
      }, 1500);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  // [需求#6] iOS/Android 添加到主屏幕后台推送：是否已开启系统通知提醒
  const [notifyPerm, setNotifyPerm] = useState(currentNotifyPermission());
  // 全局授权弹窗（NotifyPermissionPrompt）改了权限后，同步刷新下面的引导横幅
  useEffect(() => {
    const sync = () => setNotifyPerm(currentNotifyPermission());
    window.addEventListener('notify-perm-changed', sync);
    return () => window.removeEventListener('notify-perm-changed', sync);
  }, []);
  const handleEnableNotify = async () => {
    const ok = await ensurePushSubscription();
    setNotifyPerm(currentNotifyPermission());
    if (ok) showToast('已开启系统通知提醒', 'success');
    else if (currentNotifyPermission() === 'denied') showToast('通知权限已被拒绝，请在系统设置中开启', 'error');
  };

  function checkWorkTimeEnded(settings) {
    if (!settings) return false;
    // 必须用北京时间分钟数，不能用 new Date().getHours()（依赖设备时区，UTC服务器会偏差8小时）
    return bjMinuteOfDay() >= settings.endHour * 60 + settings.endMinute;
  }

  const fetchWorkSettings = useCallback(async () => {
    try {
      const res = await api.get('/admin/settings/work-hours');
      setWorkSettings(res.data);
      setWorkTimeEnded(checkWorkTimeEnded(res.data));
    } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, dataRes] = await Promise.all([
        api.get('/dispatch/my-summary'),
        api.get('/dispatch/my-data'),
      ]);
      setSummary(summaryRes.data);
      const newData = dataRes.data.data || [];
      setActiveData(newData);
      // 超过 20 条自动折叠，减少页面高度，让图片管理区域始终可见
      setDataCollapsed(newData.length > 20);
      setHasActiveTask(dataRes.data.hasActiveTask);
      setActiveBatchId(dataRes.data.batch?.batchId || null);
      setDataExpired(dataRes.data.isExpired || false);
      setDataExpiryAt(dataRes.data.expiryAt || null);
    } catch (err) {
      if (err.response?.status !== 401) showToast('加载失败', 'error');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  // 拉取今日上传分组列表
  const fetchUploadGroups = useCallback(async () => {
    try {
      const res = await api.get('/images/my-upload-groups');
      setUploadGroups(res.data.groups || []);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); fetchWorkSettings(); fetchUploadGroups(); }, [fetchData, fetchWorkSettings, fetchUploadGroups]);

  // 首次用户手势时解锁音频（页面任意点击/触摸均触发，{ once } 自动移除）
  useEffect(() => {
    document.addEventListener('click',    unlockAudio, { once: true });
    document.addEventListener('touchend', unlockAudio, { once: true });
    return () => {
      document.removeEventListener('click',    unlockAudio);
      document.removeEventListener('touchend', unlockAudio);
    };
  }, []);

  // 窗口缩放时把悬浮球收回可视区内
  useEffect(() => {
    const onResize = () => setFabPos(p => clampFab(p.x, p.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 悬浮球 Pointer 事件处理（mouse + touch 统一）
  const onFabPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    fabDrag.current = { active: false, startX: e.clientX, startY: e.clientY, originX: fabPos.x, originY: fabPos.y };
  };
  const onFabPointerMove = (e) => {
    const d = fabDrag.current;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.active && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.active = true;
    setFabPos(clampFab(d.originX + dx, d.originY + dy));
  };
  const onFabPointerUp = (e) => {
    const d = fabDrag.current;
    if (!d.active) {
      // 位移 < 阈值 → 视为点击
      openUploadFree();
    } else {
      // 拖拽结束 → 持久化最终位置
      const pos = clampFab(d.originX + e.clientX - d.startX, d.originY + e.clientY - d.startY);
      setFabPos(pos);
      try { localStorage.setItem(FAB_KEY, JSON.stringify(pos)); } catch {}
    }
    d.active = false;
  };

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (['data_refresh','settlement_updated','pool_updated','stats_refresh'].includes(event)) fetchData();
      if (event === 'work_hours_updated') fetchWorkSettings();
      if (event === 'work_time_ended') setWorkTimeEnded(true);
      if (event === 'push_message' && payload) {
        addPushes([payload]);
        // 提示音：前台后台都播
        if (supportsAudio) playChime();
        // 页内横幅：前台/后台都显示，3.5s 后自动消失
        // 二次审查类消息（指派/审核结果/打回重录）不弹页内横幅：
        // 用户站内使用时无需额外打扰（「我的→二次审查任务」角标已可见），
        // 站外提醒统一交给系统级 Web Push 横幅（sw.js 按可见窗口判断，见其注释）
        if (payload.type !== 'video_review') {
          setPushBanner(payload);
          if (pushBannerTimer.current) clearTimeout(pushBannerTimer.current);
          pushBannerTimer.current = setTimeout(() => setPushBanner(null), 3500);
        }
        // 系统横幅统一由 Service Worker 负责（sw.js 会检测窗口可见性，前台自动跳过）
        // JS 侧不再调用 Notification API，避免 SW + JS 同时弹出两条横幅
      }
      if (event === 'image_stats_refresh') fetchUploadGroups();
      if (event === 'ocr_progress') {
        // 逐张进度：只刷新分组列表（轻量）
        fetchUploadGroups();
      }
      if (event === 'ocr_batch_done') {
        fetchUploadGroups();
        const s = payload?.stats || {};
        const parts = [];
        if (s.qualified > 0) parts.push(`成功 ${s.qualified} 组`);
        if (s.duplicate > 0) parts.push(`重复 ${s.duplicate} 组`);
        if ((s.invalid || 0) + (s.failed || 0) > 0) parts.push(`待处理 ${(s.invalid||0)+(s.failed||0)} 张`);
        if (parts.length > 0) {
          showToast(`OCR 识别完成：${parts.join('，')}`, 'success');
        }
        // 持久气泡：引导用户去 OCR 页面查看
        if (ocrBannerTimer.current) clearTimeout(ocrBannerTimer.current);
        setOcrDoneBanner({ stats: s, groupUUID: payload?.groupUUID });
        ocrBannerTimer.current = setTimeout(() => setOcrDoneBanner(null), 12000);
      }
    });
  }, [subscribe, fetchData, fetchWorkSettings, fetchUploadGroups, addPushes]);

  // 离线补拉：进入工作台时把未读推送一次性取出，按时间正序入队（先发先弹）
  useEffect(() => {
    if (!user) return;
    api.get('/notifications/mine')
      .then(res => {
        const unread = (res.data?.notifications || []).filter(n => !n.is_read);
        if (unread.length > 0) addPushes(unread);
      })
      .catch(() => {});
    // 仅首次进入时拉取
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 完成任务核心（由弹窗调用）────────────────────────────────────────────
  const doComplete = useCallback(async () => {
    try {
      await api.post('/dispatch/complete');
      showToast('任务已完成', 'success');
      setUploadModal(null);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || '完成失败', 'error');
      throw err;
    }
  }, [showToast, fetchData]);

  // ── 点击"完成"：直接完成任务，不再弹上传弹窗 ──────────────────────────
  const handleComplete = async () => {
    if (!hasActiveTask || !activeBatchId) return;
    // 完成前记录批次信息，成功后用于弹上传引导
    const sorted = [...(summary?.batches || [])].sort((a, b) =>
      (a.created_at || '').localeCompare(b.created_at || ''));
    const idx = sorted.findIndex(b => b.batch_id === activeBatchId);
    const label = idx >= 0
      ? `第${idx + 1}批（${sorted[idx].quantity_dispatched}条）`
      : '当前批次';
    const batchId = activeBatchId;
    try {
      await doComplete();
      setPostCompleteUpload({ batchId, batchLabel: label });
    } catch {}
  };

  // 能不能上传：昨天或今天接过量，或还有没点“完成”的批次（点了“完成任务”后照样能继续上传）。
  // 以服务端 /dispatch/my-summary 返回的 uploadEligible 为准（和上传接口同一个函数，规则改一处两边一致）；
  // 服务端没返回这个字段时退回到旧口径（今日已接 / 有未完成任务）。summary 还没加载出来时不拦（服务端会兜底校验）。
  // 前台（这个工作台）所有角色都按同一规则锁，管理员 / 代理商也一样（和证券一致）；后台代传、后台接口不限制。
  const dispatchedToday = Math.max(summary?.todayDispatched || 0, summary?.activeDispatched || 0) > 0 || hasActiveTask;
  const uploadEligible = typeof summary?.uploadEligible === 'boolean' ? summary.uploadEligible : dispatchedToday;
  const uploadNeedDispatch = !!summary && !uploadEligible;

  // ── 打开「上传图片」：每次生成新 UUID，不绑定 batchId ─────────────────
  const openUploadFree = () => {
    // 工作时间已结束，禁止普通用户上传（管理员/代理不受限）
    if (workTimeEnded && user?.role !== 'admin') {
      const endH = workSettings?.endHour   ?? 20;
      const endM = workSettings?.endMinute ?? 0;
      const fmt  = n => String(n).padStart(2, '0');
      showToast(`每日 ${fmt(endH)}:${fmt(endM)} 后不可上传图片，请于次日 00:00 后再试`, 'error');
      return;
    }
    // 昨天和今天都没接过量：不能上传（防止无接量提交假单）
    if (uploadNeedDispatch) {
      showToast('昨天和今天都没有接量，请先接量后再上传图片', 'error');
      return;
    }
    const groupUUID =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setUploadModal({ groupUUID, mode: 'upload' });
  };

  // 弹窗关闭后刷新分组列表
  const handleModalClose = () => {
    setUploadModal(null);
    fetchUploadGroups();
  };

  // ── 编辑分组：拉取该 upload_group 的图片 → 显示缩略图列表 ─────────────
  const handleCorrectionOpen = async (group) => {
    setCorrectionLoading(true);
    setCorrection({ groupId: group.id, batchLabel: `第 ${group.seq} 批`, images: [] });
    try {
      const res = await api.get(`/images/upload-group/${group.id}`);
      setCorrection({
        groupId: group.id,
        groupUUID: res.data.groupUUID,  // 存 UUID，补充上传时复用同一批次
        batchId: null,
        batchLabel: `第 ${group.seq} 批`,
        images: res.data.images || []
      });
    } catch {
      showToast('加载图片失败', 'error');
      setCorrection(null);
    } finally {
      setCorrectionLoading(false);
    }
  };
  const handleCorrectionClose = () => {
    setCorrection(null);
    fetchUploadGroups();
  };

  // ── 纠错：删除单张图片 ─────────────────────────────────────────────────
  const handleCorrectionDelete = async (img) => {
    const ok = await confirm(`确认删除「${img.originalName || '这张图片'}」？`, { detail: '删除后可点击"+替换"上传新图。', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/images/${img.id}`);
      setCorrection(c => c ? { ...c, images: c.images.filter(i => i.id !== img.id) } : c);
      showToast('已删除', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  // ── 纠错：替换单张图片（弹文件选择 → 上传到 /:id/replace）──────────────
  const handleCorrectionReplace = async (img) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 15 * 1024 * 1024) { showToast('图片不能超过 15MB', 'error'); return; }
      try {
        const fd = new FormData();
        fd.append('image', file);
        const res = await api.post(`/images/${img.id}/replace`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        // 用接口返回的新图替换列表中的旧图
        setCorrection(c => c ? {
          ...c,
          images: c.images.map(i => i.id === img.id
            ? { ...i, id: res.data.newImage.id, url: res.data.newImage.url, originalName: res.data.newImage.originalName, uploadedAt: new Date().toISOString() }
            : i)
        } : c);
        showToast('替换成功', 'success');
      } catch (err) {
        showToast(err.response?.data?.error || '替换失败', 'error');
      }
    };
    input.click();
  };

  // ── 编辑：继续上传图片到当前分组（复用同一 groupUUID，不新建批次）──────
  const handleCorrectionAdd = () => {
    if (!correction) return;
    if (correction.groupUUID) {
      // 复用已有批次的 UUID，上传的图片会归入同一组
      setUploadModal({ groupUUID: correction.groupUUID, mode: 'supplement' });
    } else {
      // 兜底：无 UUID 时才新建（理论上不会走到这里）
      openUploadFree();
    }
  };

  // 点击手机号：复制号码 + 跳转微信
  const handlePhoneTap = async (phone, itemId) => {
    if (dataExpired) { showToast('数据已过期，无法复制', 'error'); return; }
    // 先复制手机号
    try {
      await navigator.clipboard.writeText(phone);
    } catch {
      const el = document.createElement('textarea');
      el.value = phone;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    showToast('号码已复制，正在跳转微信...', 'success');
    // 用 <a> click 触发 scheme：iOS / Android / 微信内置浏览器均能直接唤起微信，无确认弹窗
    const a = document.createElement('a');
    a.href = 'weixin://';
    a.target = '_self';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
    // 标记为已点击
    if (itemId != null) {
      setClickedPhones(prev => {
        const next = new Set(prev);
        next.add(itemId);
        try { localStorage.setItem('ws_clicked_phones', JSON.stringify([...next])); } catch {}
        return next;
      });
      setCopiedId(itemId);
      setTimeout(() => setCopiedId(c => (c === itemId ? null : c)), 1500);
    }
  };
  const handleCopyOne = async (item) => {
    if (dataExpired) { showToast('数据已过期，无法复制', 'error'); return; }
    await copy(item.customer_phone || '', '已复制');
    setCopiedId(item.id);
    // 标记为已点击/复制
    setClickedPhones(prev => {
      const next = new Set(prev);
      next.add(item.id);
      try { localStorage.setItem('ws_clicked_phones', JSON.stringify([...next])); } catch {}
      return next;
    });
    setTimeout(() => setCopiedId(null), 1500);
  };
  const handleCopyAll = async () => {
    if (!activeData.length) return;
    if (dataExpired) { showToast('数据已过期，无法复制', 'error'); return; }
    const text = activeData.map(i => i.customer_phone || i.customer_name || '').filter(Boolean).join('\n');
    await copy(text, `已复制全部 ${activeData.length} 条`);
  };

  // 转发当前批次为 xlsx 表格文档（Excel 直接打开，可转发微信/QQ；数据只有手机号）
  const handleShareCurrent = async () => {
    if (!activeData.length) return;
    if (dataExpired) { showToast('数据已过期，无法转发', 'error'); return; }
    const myName = user?.name || user?.username || user?.phone || '我';
    const expiryNote = calcExpiryFromRecords(activeData, 'data_imported_at');
    const res = await shareFile({
      filename: `${myName}-${activeData.length}条-${filenameTimestamp()}.xlsx`,
      text: buildXlsx(activeData.map(i => ({ phone: i.customer_phone || '' })), expiryNote),
      ext: 'xlsx',
    });
    if (res === 'shared') showToast('已生成表格文档，请选择微信/QQ等发送', 'success');
    else if (res === 'download') showToast('当前浏览器不支持弹分享面板，已改为直接下载表格文件', 'info');
  };

  // 复制当前批次为文本（一条一行）
  const handleCopyText = async () => {
    if (!activeData.length) return;
    if (dataExpired) { showToast('数据已过期，无法复制', 'error'); return; }
    const text = buildNumberText(activeData.map(i => i.customer_phone || i.customer_name || ''));
    await copy(text, `已复制全部 ${activeData.length} 条（文本格式）`);
  };

  if (loading) return (
    <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <p style={{ color: 'var(--c-text-secondary)' }}>加载中...</p>
    </div>
  );


  return (
    <div className="page">
      {/* 推送消息横幅：前台后台均显示，3.5s 自动消失，点击关闭 */}
      {pushBanner && (
        <div
          onClick={() => setPushBanner(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
            background: '#1e293b', color: '#fff',
            padding: '12px 16px',
            display: 'flex', gap: 10, alignItems: 'flex-start',
            boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
            cursor: 'pointer',
          }}
        >
          <Bell size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>
              {pushBanner.title || '系统通知'}
            </div>
            {pushBanner.content && (
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2, lineHeight: 1.5,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pushBanner.content}
              </div>
            )}
          </div>
          <X size={16} style={{ flexShrink: 0, opacity: 0.7, marginTop: 2 }} />
        </div>
      )}

      {/* OCR 识别完成气泡通知：12秒后自动消失，点击跳转 OCR 页 */}
      {ocrDoneBanner && (() => {
        const s = ocrDoneBanner.stats || {};
        const parts = [];
        if (s.qualified > 0) parts.push(`成功 ${s.qualified} 组`);
        if (s.duplicate > 0) parts.push(`重复 ${s.duplicate} 组`);
        if ((s.invalid || 0) + (s.failed || 0) > 0) parts.push(`待处理 ${(s.invalid||0)+(s.failed||0)} 张`);
        return (
          <div style={{
            position: 'fixed', bottom: 72, left: 12, right: 12, zIndex: 9990,
            background: '#1e293b', color: '#fff',
            borderRadius: 14, padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
            animation: 'slideUpBanner .25s ease',
          }}>
            <ScanLine size={18} color="#60a5fa" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>OCR 识别完成</div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{parts.join('，') || '识别已完成'}</div>
            </div>
            <button
              onClick={() => { setOcrDoneBanner(null); navigate('/history'); }}
              style={{
                background: 'var(--c-primary)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >去 OCR 查看</button>
            <button
              onClick={() => setOcrDoneBanner(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}
            ><X size={14} /></button>
          </div>
        );
      })()}

      <div className="header">
        <span>工作台</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <TutorialButton />
          {isAdminOrAgent && (
            <button className="btn btn-sm btn-outline" onClick={() => navigate('/admin')} style={{ fontSize: 12 }}>
              <Settings size={14} /> 管理后台
            </button>
          )}
        </div>
      </div>

      {/* 排五 / 证券 业务切换 */}
      <div style={{ padding: '10px 16px 0' }}><BizSwitch /></div>

      {bizMode === 'puzzle' ? (
        <div style={{ padding: '12px 16px' }}><PuzzleWorkspace /></div>
      ) : (
      <div style={{ padding: '12px 16px' }}>

        {/* 通知引导横幅：
              - default + 非 APK（浏览器/PWA）：显示手动开启按钮
              - denied：不论 APK 还是浏览器，引导去系统设置开启
              - APK 内 default：自动弹权限框，不显示横幅（避免重复）       */}
        {supportsPush && notifyPerm === 'default' && (
          <div className="card" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            background: 'var(--c-primary-light)', marginBottom: 12,
          }}>
            <Bell size={16} color="var(--c-primary)" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12, color: 'var(--c-text-secondary)' }}>
              开启通知后，即使把网页切到后台也能收到新消息提醒
            </span>
            <button className="btn btn-primary btn-sm" style={{ flexShrink: 0, fontSize: 12 }} onClick={handleEnableNotify}>
              开启通知
            </button>
          </div>
        )}
        {supportsPush && notifyPerm === 'denied' && (
          <div className="card" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            background: 'var(--c-danger-light, #fff1f0)', marginBottom: 12,
          }}>
            <Bell size={16} color="var(--c-danger)" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12, color: 'var(--c-text-secondary)' }}>
              通知权限已关闭，收不到新消息提醒。请到系统设置 → 应用 → React → 通知 里开启
            </span>
          </div>
        )}

        {/* 统计卡 */}
        <div className="card" style={{ display: 'flex', gap: 0 }}>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-value" style={{ color: 'var(--c-primary)' }}>
              {summary
                ? Math.max(summary.todayDispatched || 0, summary.activeDispatched || 0)
                : 0}
            </div>
            <div className="stat-label">已领条数</div>
          </div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-value" style={{ color: 'var(--c-success)' }}>{summary?.remainingPool || 0}</div>
            <div className="stat-label">剩余可领</div>
          </div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-value" style={{ color: 'var(--c-accent)' }}>{summary?.todaySuccess || 0}</div>
            <div className="stat-label">今日成功</div>
          </div>
        </div>

        {/* 超时横幅 */}
        {workTimeEnded && hasActiveTask && (
          <div style={{
            background: 'rgba(255,59,48,0.06)', border: '1px solid rgba(255,59,48,0.25)',
            borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start'
          }}>
            <AlertTriangle size={18} color="var(--c-danger)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--c-danger)' }}>工作时间已结束</div>
              <div style={{ fontSize: 13, color: 'var(--c-danger)', opacity: 0.8, marginTop: 2, lineHeight: 1.5 }}>
                超时后添加的好友不计入本次结算，请联系负责人核对。
              </div>
            </div>
          </div>
        )}

        {/* 任务区 */}
        {hasActiveTask ? (
          <div className="card card-no-bounce" style={{ border: '1.5px solid rgba(0,122,255,0.2)', position: 'sticky', top: 'calc(53px + env(safe-area-inset-top, 0px))', zIndex: 40, touchAction: 'pan-y' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>当前任务</div>
                <span className="badge badge-warning" style={{ marginTop: 4 }}>进行中 · {activeData.length} 条</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-success btn-sm" onClick={handleComplete}>
                  <CheckCircle2 size={15} /> 完成
                </button>
              </div>
            </div>

            {/* 数据有效期提示 / 过期锁定横幅 */}
            {dataExpired ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', marginBottom: 10,
                background: 'rgba(255,59,48,0.07)', border: '1px solid rgba(255,59,48,0.3)',
                borderRadius: 8, fontSize: 12, color: 'var(--c-danger)',
              }}>
                <Lock size={14} style={{ flexShrink: 0 }} />
                <span>数据已过期（有效期至 <strong>{dataExpiryAt}</strong>），无法查看或转发，请联系管理员处理。</span>
              </div>
            ) : dataExpiryAt ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', marginBottom: 10,
                background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)',
                borderRadius: 8, fontSize: 12, color: 'var(--c-accent)',
              }}>
                <Clock size={13} style={{ flexShrink: 0 }} />
                <span>数据有效期至<strong>{dataExpiryAt}</strong>，非有效期内提交无效</span>
              </div>
            ) : null}

            <button onClick={handleCopyAll} disabled={dataExpired} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 6, padding: '9px 0', marginBottom: 10,
              background: dataExpired ? 'var(--c-bg)' : 'var(--c-primary-light)',
              border: `1px solid ${dataExpired ? 'var(--c-border)' : 'rgba(0,122,255,0.2)'}`,
              borderRadius: 8, fontSize: 13, fontWeight: 600,
              color: dataExpired ? 'var(--c-text-tertiary)' : 'var(--c-primary)',
              cursor: dataExpired ? 'not-allowed' : 'pointer', opacity: dataExpired ? 0.6 : 1,
            }}>
              {dataExpired ? <Lock size={15} /> : <ClipboardList size={15} />}
              {dataExpired ? '数据已过期' : `一键复制全部 ${activeData.length} 条`}
            </button>

            {/* 转发为文档：生成 CSV 表格，可转发微信/QQ */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button onClick={handleShareCurrent} disabled={dataExpired} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '9px 0',
                background: dataExpired ? 'var(--c-text-tertiary)' : 'var(--c-success)', color: '#fff',
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: dataExpired ? 'not-allowed' : 'pointer', border: 'none',
                opacity: dataExpired ? 0.5 : 1,
              }}>
                {dataExpired ? <Lock size={15} /> : <Share2 size={15} />} 转发文档
              </button>
              <button onClick={handleCopyText} disabled={dataExpired} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '9px 0',
                background: 'var(--c-bg)', color: dataExpired ? 'var(--c-text-tertiary)' : 'var(--c-text)',
                border: '1px solid var(--c-border)', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: dataExpired ? 'not-allowed' : 'pointer', opacity: dataExpired ? 0.5 : 1,
              }}>
                <Copy size={15} /> 复制文本
              </button>
            </div>

            {/* 折叠切换入口 */}
            {activeData.length > 20 && (
              <button
                onClick={() => !dataExpired && setDataCollapsed(v => !v)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 5, padding: '7px 0', marginBottom: 4,
                  background: 'var(--c-bg)', border: '1px solid var(--c-border)',
                  borderRadius: 8, fontSize: 12, color: 'var(--c-text-secondary)',
                  cursor: dataExpired ? 'not-allowed' : 'pointer'
                }}
              >
                {dataCollapsed
                  ? <><ChevronDown size={14} /> 展开全部 {activeData.length} 条</>
                  : <><ChevronUp size={14} /> 折叠列表</>
                }
              </button>
            )}

            {!dataCollapsed && (dataExpired ? (
              /* 过期时隐藏号码，只显示占位行 */
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '24px 0 12px', gap: 8,
                color: 'var(--c-text-tertiary)', fontSize: 13,
              }}>
                <Lock size={28} color="var(--c-danger)" style={{ opacity: 0.45 }} />
                <span>数据已过期，内容已隐藏</span>
              </div>
            ) : activeData.map((item, i) => {
              const phone = item.customer_phone || item.customer_name || '';
              const isClicked = clickedPhones.has(item.id);
              return (
                <div key={item.id} style={{
                  padding: '11px 0', borderBottom: '0.5px solid var(--c-border)',
                  display: 'flex', alignItems: 'center', gap: 12
                }}>
                  <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)', minWidth: 28 }}>#{i + 1}</span>
                  <span onClick={() => phone && handlePhoneTap(phone, item.id)}
                    style={{
                      flex: 1, fontSize: 16, fontWeight: 600, letterSpacing: '0.5px',
                      cursor: phone ? 'pointer' : 'default',
                      color: isClicked ? 'var(--c-text-tertiary)' : 'var(--c-primary)',
                      textDecoration: isClicked ? 'line-through' : 'none',
                      opacity: isClicked ? 0.55 : 1,
                      transition: 'color .2s, opacity .2s',
                    }}>
                    {phone}
                  </span>
                  <button onClick={() => handleCopyOne({ ...item, customer_phone: phone })} style={{
                    background: copiedId === item.id ? 'rgba(52,199,89,0.1)' : 'transparent',
                    border: `1px solid ${copiedId === item.id ? 'rgba(52,199,89,0.4)' : 'var(--c-border)'}`,
                    borderRadius: 8, padding: '5px 12px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
                    color: copiedId === item.id ? 'var(--c-success)' : 'var(--c-text-secondary)', flexShrink: 0
                  }}>
                    <Copy size={13} />
                    {copiedId === item.id ? '已复制' : '复制'}
                  </button>
                </div>
              );
            }))}

            {/* 折叠时显示已折叠提示 */}
            {dataCollapsed && (
              <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 13, color: 'var(--c-text-tertiary)' }}>
                列表已折叠，共 {activeData.length} 条
              </div>
            )}
          </div>
        ) : (
          <div className="card empty-state" style={{ padding: '40px 20px' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <PackageOpen size={48} color="var(--c-text-secondary)" strokeWidth={1.2} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 16, color: 'var(--c-text)' }}>当前无进行中的任务</p>
            <p style={{ fontSize: 13, marginTop: 6 }}>前往「接量」页面提交接量申请</p>
            <button className="btn btn-primary" onClick={() => navigate('/request')}
              style={{ marginTop: 20, maxWidth: 200, margin: '20px auto 0' }}>
              去接量 <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── 图片管理（始终显示，不依赖是否有任务）─────────────────────────── */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Images size={18} color="var(--c-primary)" />
            <span style={{ fontSize: 15, fontWeight: 700 }}>图片管理</span>
          </div>

          {uploadGroups.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--c-text-tertiary)', textAlign: 'center', padding: '12px 0 4px' }}>
              今日暂无上传记录
            </p>
          )}

          {uploadGroups.map(group => {
            const ocr = group.ocr || {};
            const ocrDone = ocr.done && ocr.total > 0;
            const ocrRunning = !ocr.done && ocr.total > 0;
            const isExpanded = !!expandedGroups[group.id];
            return (
            <div key={group.id} style={{
              border: '1px solid var(--c-border)',
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 8,
              background: 'var(--c-surface)',
              opacity: group.is_exported ? 0.6 : 1
            }}>
              {/* 批次头 */}
              <div
                style={{ padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: ocrDone ? 'pointer' : 'default' }}
                onClick={() => ocrDone && setExpandedGroups(p => ({ ...p, [group.id]: !p[group.id] }))}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>第 {group.seq} 批 · {group.image_count} 张</span>
                    {group.is_exported && <span style={{ fontSize: 10, background: 'var(--c-bg)', color: 'var(--c-text-secondary)', borderRadius: 6, padding: '1px 6px' }}>已导出</span>}
                    {ocrRunning && (
                      <span style={{ fontSize: 10, color: 'var(--c-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Loader size={9} className="spin" /> 识别中…
                      </span>
                    )}
                    {ocrDone && <span style={{ fontSize: 10, color: 'var(--c-success)', display: 'inline-flex', alignItems: 'center', gap: 2 }}><svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3'><polyline points='20 6 9 17 4 12'/></svg>识别完成</span>}
                  </div>
                  {ocrRunning && (
                    <div style={{ height: 3, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden', marginBottom: 3 }}>
                      <div style={{ height: '100%', background: 'var(--c-primary)', borderRadius: 2, width: `${Math.round(((ocr.qualified||0)+(ocr.duplicate||0)+(ocr.invalid||0)+(ocr.failed||0)) / ocr.total * 100)}%`, transition: 'width .5s' }} />
                    </div>
                  )}
                  {ocrDone && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {ocr.qualified > 0 && <span style={{ fontSize: 10, background: 'rgba(52,199,89,.12)', color: 'var(--c-success)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>成功 {ocr.qualified} 组</span>}
                      {ocr.duplicate > 0 && <span style={{ fontSize: 10, background: 'rgba(255,149,0,.12)', color: 'var(--c-accent)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>重复 {ocr.duplicate} 组</span>}
                      {ocr.invalid > 0 && <span style={{ fontSize: 10, background: 'rgba(255,59,48,.1)', color: 'var(--c-danger)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>无效 {ocr.invalid} 组</span>}
                      {ocr.failed > 0 && <span style={{ fontSize: 10, background: 'var(--c-bg)', color: 'var(--c-text-secondary)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>失败 {ocr.failed} 张</span>}
                    </div>
                  )}
                  {!ocr.total && !group.is_exported && (
                    <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>等待识别…</span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  {!group.is_exported && (
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button className="btn btn-outline btn-sm"
                        style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 3 }}
                        onClick={e => { e.stopPropagation(); handleCorrectionOpen(group); }}>
                        <FolderPen size={12} /> 编辑
                      </button>
                      <button className="btn btn-outline btn-sm"
                        disabled={deletingGroupId === group.id || ocrRunning}
                        style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 3, color: 'var(--c-danger)', borderColor: 'var(--c-danger)', opacity: (deletingGroupId === group.id || ocrRunning) ? 0.5 : 1 }}
                        onClick={async e => {
                          e.stopPropagation();
                          const ok = await confirm(`确认删除第 ${group.seq} 批全部 ${group.image_count} 张图片？`, { detail: '此操作不可恢复。', danger: true });
                          if (!ok) return;
                          setDeletingGroupId(group.id);
                          try {
                            await api.delete(`/images/upload-group/${group.id}`);
                            showToast('已删除整批图片', 'success');
                            setTimeout(fetchUploadGroups, 300);
                          } catch (err) {
                            showToast(err.response?.data?.error || '删除失败', 'error');
                          } finally {
                            setDeletingGroupId(null);
                          }
                        }}>
                        {deletingGroupId === group.id ? <Loader size={11} className="spin" /> : <X size={11} />}
                        {deletingGroupId === group.id ? '删除中…' : '删除'}
                      </button>
                    </div>
                  )}
                  {ocrDone && <ChevronDown size={14} color="var(--c-text-tertiary)" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '.18s' }} />}
                </div>
              </div>

              {/* 展开：配对成功 + 待处理 */}
              {isExpanded && ocrDone && (
                <OcrGroupDetail
                  groupUUID={group.groupUUID || group.id}
                  imagesDeleted={!!group.is_exported}
                  onManualFill={(imageId, prefillPhone) => { setOcrManualTarget({ imageId, groupId: group.id }); setOcrManualPhone(prefillPhone || ''); }}
                />
              )}
            </div>
            );
          })}

        </div>

        {/* 上传图片：悬浮球，可拖动，始终可见，不会被任务列表遮挡 */}
        {(() => {
          const timeBlocked = workTimeEnded && user?.role !== 'admin';   // 超过每日截止时间
          const uploadBlocked = timeBlocked || uploadNeedDispatch;       // 置灰：过了截止时间，或今天还没接量
          const endH = workSettings?.endHour   ?? 20;
          const endM = workSettings?.endMinute ?? 0;
          const fmt  = n => String(n).padStart(2, '0');
          return (
            <button
              onPointerDown={onFabPointerDown}
              onPointerMove={onFabPointerMove}
              onPointerUp={onFabPointerUp}
              style={{
                position: 'fixed',
                left: fabPos.x,
                top:  fabPos.y,
                zIndex: 50,
                width: FAB_SIZE,
                height: FAB_SIZE,
                borderRadius: '50%',
                background: uploadBlocked
                  ? 'linear-gradient(135deg, #8e8e93 0%, #636366 100%)'
                  : 'linear-gradient(135deg, #0A84FF 0%, #0055D4 100%)',
                border: 'none',
                cursor: 'grab',
                touchAction: 'none',
                userSelect: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                boxShadow: uploadBlocked
                  ? '0 4px 16px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.10)'
                  : fabPulse
                    ? '0 0 0 8px rgba(0,122,255,0.25), 0 4px 20px rgba(0,122,255,0.6)'
                    : '0 4px 16px rgba(0,122,255,0.45), 0 1px 4px rgba(0,0,0,0.15)',
                opacity: uploadBlocked ? 0.85 : 1,
                transform: fabPulse ? 'scale(1.12)' : 'scale(1)',
                transition: 'box-shadow 0.2s, transform 0.2s',
              }}
              title={timeBlocked
                ? `${fmt(endH)}:${fmt(endM)} 后不可上传，次日 00:00 开放`
                : uploadNeedDispatch
                  ? '昨天和今天都没有接量，接量后才能上传'
                  : '上传图片（可拖动调整位置）'}
            >
              {uploadBlocked
                ? <><Lock size={18} color="white" strokeWidth={2.5} />
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)', lineHeight: 1, marginTop: 1 }}>{timeBlocked ? '已锁定' : '需接量'}</span>
                  </>
                : <UploadCloud size={24} color="white" strokeWidth={2} />
              }
            </button>
          );
        })()}
      </div>
      )}

      {/* 图片上传弹窗（完成 or 补充） */}
      {uploadModal && (
        <BatchUploadModal
          groupUUID={uploadModal.groupUUID}
          mode={uploadModal.mode}
          onComplete={uploadModal.mode === 'complete' ? doComplete : undefined}
          onClose={handleModalClose}
        />
      )}

      {/* ── 纠错弹窗：展示本批已提交图片，可预览 / 删除 / 替换 ─────────── */}
      {correction && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.62)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
        }} onClick={correctionLoading ? undefined : handleCorrectionClose}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--c-surface)',
              borderRadius: '22px 22px 0 0',
              width: '100%', maxWidth: 520,
              maxHeight: '93vh', overflowY: 'auto',
              padding: '20px 16px 32px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Edit3 size={20} color="var(--c-accent)" />
                <span style={{ fontSize: 16, fontWeight: 700 }}>纠错</span>
              </div>
              <button onClick={handleCorrectionClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} color="var(--c-text-secondary)" />
              </button>
            </div>

            <div style={{
              fontSize: 13, color: 'var(--c-text-secondary)',
              padding: '8px 12px', background: 'var(--c-bg)', borderRadius: 10, marginBottom: 14,
              lineHeight: 1.6
            }}>
              {correction.batchLabel ?? correction.batchId}
              <div style={{ marginTop: 2, fontSize: 12, opacity: 0.85 }}>
                点击小图可放大预览；可删除错图或替换成新图。
              </div>
            </div>

            {correctionLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-secondary)' }}>
                <Loader size={24} className="spin" style={{ margin: '0 auto 10px', display: 'block' }} />
                加载图片中...
              </div>
            ) : correction.images.length === 0 ? (
              <div className="empty-state" style={{ padding: 30 }}>
                <Camera size={36} color="var(--c-text-tertiary)" strokeWidth={1.2} style={{ margin: '0 auto 10px', display: 'block' }} />
                <p style={{ fontSize: 14, color: 'var(--c-text-secondary)' }}>本批次还没有图片</p>
              </div>
            ) : (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
                marginBottom: 16
              }}>
                {correction.images.map((img, idx) => (
                  <div key={img.id} style={{ position: 'relative', aspectRatio: '1' }}>
                    <img
                      src={img.url}
                      alt={img.originalName || ''}
                      onClick={() => setLightbox({ images: correction.images, idx })}
                      style={{
                        width: '100%', height: '100%', objectFit: 'cover',
                        borderRadius: 10, border: '1px solid var(--c-border)',
                        display: 'block', cursor: 'zoom-in'
                      }}
                    />
                    {/* 左下：序列号 */}
                    <span style={{
                      position: 'absolute', left: 5, bottom: 5,
                      background: 'rgba(0,0,0,0.55)', color: 'white',
                      fontSize: 10, padding: '2px 6px', borderRadius: 6
                    }}>{idx + 1}</span>
                    {/* 右上：替换按钮 */}
                    <button
                      onClick={() => handleCorrectionReplace(img)}
                      title="替换此图片"
                      style={{
                        position: 'absolute', top: 5, right: 5,
                        width: 24, height: 24, borderRadius: '50%', padding: 0,
                        background: 'rgba(0,122,255,0.9)', border: '1.5px solid white',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <Edit3 size={11} color="white" />
                    </button>
                    {/* 右下：删除按钮（已识别成功不可删；普通用户已导出也不可删） */}
                    {img.ocrResult !== 'qualified' && img.ocrResult !== 'manual_set' &&
                     !(img.packaged && user?.role === 'user') && (
                    <button
                      onClick={() => handleCorrectionDelete(img)}
                      title="删除此图片"
                      style={{
                        position: 'absolute', right: 5, bottom: 5,
                        width: 24, height: 24, borderRadius: '50%', padding: 0,
                        background: 'rgba(255,59,48,0.9)', border: '1.5px solid white',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <X size={11} color="white" />
                    </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 底部操作 */}
            {!correctionLoading && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleCorrectionClose} style={{
                  flex: 1, height: 48, borderRadius: 14,
                  background: 'var(--c-bg)', color: 'var(--c-text)',
                  border: '1px solid var(--c-border)', fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}>完成</button>
                <button onClick={handleCorrectionAdd} style={{
                  flex: 1, height: 48, borderRadius: 14,
                  background: 'var(--c-primary)', color: 'white', border: 'none',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}>
                  <Camera size={15} /> 补充新图
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 灯箱：点击小图全屏预览（portal 到 body，避免被 #root/.page 容器或教程弹层遮挡） */}
      {lightbox && createPortal(
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483600,
            background: 'rgba(0,0,0,0.92)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
              background: 'rgba(255,255,255,0.1)', border: 'none',
              borderRadius: '50%', width: 36, height: 36,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <X size={18} color="white" />
          </button>

          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
            {lightbox.idx + 1} / {lightbox.images.length}
          </div>

          <img
            src={lightbox.images[lightbox.idx].url}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 12 }}
          />

          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 10 }}>
            {lightbox.images[lightbox.idx].originalName || ''}
          </div>

          {lightbox.images.length > 1 && (
            <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
              <button
                onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, idx: (l.idx - 1 + l.images.length) % l.images.length })); }}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
                  padding: '8px 20px', color: 'white', cursor: 'pointer', fontSize: 14
                }}
              >← 上一张</button>
              <button
                onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, idx: (l.idx + 1) % l.images.length })); }}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
                  padding: '8px 20px', color: 'white', cursor: 'pointer', fontSize: 14
                }}
              >下一张 →</button>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* ── 完成后上传引导弹窗 ────────────────────────────────────────────── */}
      {postCompleteUpload && createPortal(
        <div
          onClick={() => { setPostCompleteUpload(null); setFabPulse(true); setTimeout(() => setFabPulse(false), 2000); }}
          style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--c-surface)', borderRadius: '22px 22px 0 0',
            padding: '10px 20px 32px', width: '100%', maxWidth: 480,
            boxShadow: '0 -4px 32px rgba(0,0,0,0.18)',
          }}>
            {/* 拖拽把手 */}
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--c-border)', margin: '0 auto 20px' }} />
            {/* 图标 + 标题横排 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <CheckCircle2 size={36} color="var(--c-success)" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>任务已完成</div>
                <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 2 }}>请上传工作凭证截图</div>
              </div>
            </div>
            {/* 按钮区 */}
            <button
              className="btn btn-primary"
              style={{ height: 50, borderRadius: 14, fontSize: 15, marginTop: 18, marginBottom: 10 }}
              onClick={() => {
                setPostCompleteUpload(null);
                setTimeout(() => openUploadFree(), 80);
              }}
            >
              <UploadCloud size={16} /> 立即上传
            </button>
            <button
              onClick={() => { setPostCompleteUpload(null); setFabPulse(true); setTimeout(() => setFabPulse(false), 2000); }}
              style={{ display: 'block', width: '100%', padding: '10px 0', fontSize: 14, color: 'var(--c-text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              稍后上传
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* ── 站内通知收件箱（结算通知、系统通知等）：上一条 / 下一条翻页，一键全部关闭 ───── */}
      {pushList.length > 0 && (
        <PushInbox items={pushList} index={pushIdx} onIndex={goPush} onCloseOne={closePushOne} onCloseAll={closePushAll} newCount={pushNew} />
      )}
      
      {/* OCR 手动填写手机号弹窗（前台用户） */}
      {ocrManualTarget && (
        <Modal show={true} onClose={() => setOcrManualTarget(null)} title="手动填写手机号">
          <div style={{ padding: '4px 0 8px' }}>
            <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 12 }}>图片未能识别到号码，请手动输入：</p>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type="tel"
                placeholder="输入11位手机号"
                maxLength={11}
                value={ocrManualPhone}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                  setOcrManualPhone(v);
                  setOcrManualCheck(null);
                  if (v.length === 11 && /^1[3-9]\d{9}$/.test(v)) {
                    api.post('/images/check-phone', { phone: v })
                      .then(r => setOcrManualCheck(r.data))
                      .catch(() => {});
                  }
                }}
                style={{ letterSpacing: '.06em', paddingRight: 44 }}
                autoFocus
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 600, color: ocrManualPhone.length === 11 ? 'var(--c-success)' : 'var(--c-text-tertiary)', pointerEvents: 'none' }}>
                {ocrManualPhone.length}/11
              </span>
            </div>
            {ocrManualPhone.length > 0 && ocrManualPhone.length < 11 && (
              <p style={{ fontSize: 11.5, color: 'var(--c-danger)', marginTop: 5 }}>还需再输入 {11 - ocrManualPhone.length} 位</p>
            )}
            {ocrManualCheck && !ocrManualCheck.valid && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(255,59,48,.08)', color: 'var(--c-danger)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {ocrManualCheck.message}
              </div>
            )}
            {ocrManualCheck && ocrManualCheck.valid && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(52,199,89,.08)', color: 'var(--c-success)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                号码在派单库中，可以提交
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-outline" style={{ flex: 1, height: 44 }} onClick={() => { setOcrManualTarget(null); setOcrManualPhone(''); setOcrManualCheck(null); }}>取消</button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, height: 44 }}
                disabled={!/^1[3-9]\d{9}$/.test(ocrManualPhone) || ocrManualLoading || (ocrManualCheck && !ocrManualCheck.valid)}
                onClick={async () => {
                  setOcrManualLoading(true);
                  try {
                    await api.post('/images/ocr-edit/' + ocrManualTarget.imageId, { phone: ocrManualPhone });
                    showToast('已保存手机号', 'success');
                    setOcrManualTarget(null);
                    setOcrManualCheck(null);
                    setTimeout(fetchUploadGroups, 800);
                  } catch (err) { showToast(err.response?.data?.error || '保存失败', 'error'); }
                  finally { setOcrManualLoading(false); }
                }}
              >
                {ocrManualLoading ? '保存中…' : '确认填写'}
              </button>
            </div>
          </div>
        </Modal>
      )}

<TabBar mode="user" />
    </div>
  );
}
